import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Panel, Typography } from "@/components/atoms";
import { useKyvEnabled } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { clsx } from "@/utils";
import { StepIndicator } from "@/components/molecules";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { VehicleDetailsSummary } from "./common/VehicleDetailsSummary";
import { Step1VehicleInfo } from "./steps/Step1VehicleInfo";
import { Step2DriverAccess } from "./steps/Step2DriverAccess";
import { Step3VehicleDocuments } from "./steps/Step3VehicleDocuments";
import { Step3VeivoVerification } from "./steps/Step3VeivoVerification";
import { Step4Review } from "./steps/Step4Review";
import { FinishLaterModal } from "./modals/FinishLaterModal";
import { AddVehicleWizardProvider, useAddVehicleWizardContext } from "./AddVehicleWizardContext";
import { resolveActiveStep, stepSequenceFor } from "./helpers";
import { useGetVeivoValidationQuery } from "@/redux/apis/vehicle/veivoApi";

/** Keyed by the backend's `setup_step` — see stepSequenceFor in ./helpers. */
const STEP_TITLES: Record<number, { title: string; description: string }> = {
  1: { title: "Complete Vehicle Information", description: "Provide the remaining details to complete this vehicle's setup." },
  2: { title: "Driver Access", description: "Search and add drivers who will have access to this vehicle." },
  3: { title: "Vehicle Verification", description: "Verify this vehicle before completing its setup." },
  4: { title: "Review & Confirm", description: "Review all the details before adding this vehicle." },
};

/** Step 3 is a different page on each flow, so it needs a different heading:
 *  KYV verifies the vehicle, the plate flow collects the documents KYV would
 *  otherwise have collected for it. */
const DOCUMENTS_STEP_TITLE = { title: "Vehicle Documents", description: "Upload the required documents for this vehicle." };

export const AddVehicleWizard = () => {
  return (
    <AddVehicleWizardProvider>
      <WizardContent />
    </AddVehicleWizardProvider>
  );
};

const WizardContent = () => {
  const navigate = useNavigate();
  const { vehicleId, vehicle, refetchVehicle } = useAddVehicleWizardContext();
  const [currentStep, setCurrentStep] = useState(vehicle.setupStep ?? 1);
  const [showFinishLater, setShowFinishLater] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  const { enabled: kyvEnabled, isLoading: loadingKyv } = useKyvEnabled();

  // A VIN-first vehicle arrives ALREADY verified — KYV ran before it existed.
  // Without this, step 3 would start a second validation: startValidation()
  // resumes only a non-terminal one, so a 'complete' link falls through to
  // createValidation. That means a duplicate paid verification and asking the
  // operator to re-walk a car they just did.
  //
  // Skipped entirely on the plate flow: no validation can exist for a vehicle
  // added that way, and asking would be a request whose answer is never used.
  const { data: existingValidation, isLoading: loadingValidation } =
    useGetVeivoValidationQuery(vehicleId, { skip: !kyvEnabled });
  const alreadyVerified = kyvEnabled && existingValidation?.status === "complete";

  // If vehicle setup is already complete, redirect to vehicle view
  if (vehicle.setupStep == null) {
    navigate(`../${vehicleId}`, { replace: true });
    return null;
  }

  // Render nothing until we know which flow this is, and whether the vehicle
  // is already verified.
  //
  // Both booleans are false while their query is in flight, which is not the
  // same as "no" — and step 3 starts a validation from a MOUNT effect, so a
  // single render on an unresolved value was enough to mint a duplicate
  // verification. The server now refuses to create one anyway; this stops us
  // asking in the first place. The flag matters for the same reason: it picks
  // which page step 3 even is.
  if (loadingKyv || loadingValidation) return null;

  /**
   * On the KYV flow, Veivo already collected the documents this wizard used to
   * ask for. `comprehensive_insurance` (required) and `accreditation`
   * (optional) are registry rows in Veivo for every supported country, and the
   * projection writes them onto the vehicle — so the upload page was asking a
   * second time for files the operator had already provided, and showing empty
   * dropzones when they had. Review still displays whatever KYV projected, and
   * Edit Vehicle owns document management from then on.
   *
   * On the plate flow nothing has collected them, so step 3 stays.
   */
  const sequence = stepSequenceFor(kyvEnabled, alreadyVerified);
  const activeStep = resolveActiveStep(sequence, currentStep);
  const position = sequence.indexOf(activeStep) + 1;
  const isReview = activeStep === 4;

  const stepInfo = activeStep === 3 && !kyvEnabled ? DOCUMENTS_STEP_TITLE : STEP_TITLES[activeStep];

  const handleFinishLater = () => {
    setShowFinishLater(true);
  };

  const handleConfirmFinishLater = () => {
    setShowFinishLater(false);
    navigate("../");
  };

  const handleNext = async () => {
    await refetchVehicle();
    setCurrentStep(sequence[Math.min(sequence.indexOf(activeStep) + 1, sequence.length - 1)]);
  };

  const handleBack = () => {
    setCurrentStep(sequence[Math.max(sequence.indexOf(activeStep) - 1, 0)]);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return <Step1VehicleInfo vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onFinishLater={handleFinishLater} />;
      case 2:
        return <Step2DriverAccess vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onBack={handleBack} onFinishLater={handleFinishLater} />;
      case 3:
        // On KYV, only reachable when NOT yet verified — a vehicle part-way
        // through setup from before KYV became the front door. On the plate
        // flow it is every vehicle, because nothing else collects documents.
        return kyvEnabled ? (
          <Step3VeivoVerification vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onBack={handleBack} onFinishLater={handleFinishLater} />
        ) : (
          <Step3VehicleDocuments vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onBack={handleBack} onFinishLater={handleFinishLater} />
        );
      case 4:
        return <Step4Review vehicleId={vehicleId} vehicle={vehicle} onBack={handleBack} onFinishLater={handleFinishLater} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col-reverse items-start gap-3 xl:flex-row-reverse">
        {!isReview && (
          <Panel className="w-full shrink-0 xl:w-[412px]">
            <VehicleDetailsSummary vehicle={vehicle} />
          </Panel>
        )}
        <Panel className={clsx("flex w-full flex-col", isReview && "mx-auto max-w-[780px]")}>
          <div className="flex flex-col items-center">
            <StepIndicator currentStep={position} numSteps={sequence.length} className="mb-2.5" />
            <Typography variant="title">{stepInfo.title}</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              {stepInfo.description}
            </Typography>
          </div>
          <div className="mt-8">{renderStep()}</div>
        </Panel>
      </div>
      <FinishLaterModal
        open={showFinishLater}
        vehicle={vehicle}
        currentStep={position}
        totalSteps={sequence.length}
        onKeepGoing={() => setShowFinishLater(false)}
        onFinishLater={handleConfirmFinishLater}
      />
      <VehicleTypesSetupModal open={needsTypeMigration} onClose={() => navigate("../")} valid={typesValid} total={typesTotal} />
    </>
  );
};
